import * as THREE from 'three'

/**
 * Cópia das posições (e dos ids) das malhas do Fragments, para as
 * arestas.
 *
 * Porque existe. O Fragments apaga o array de CPU de cada atributo
 * logo a seguir a enviá-lo para a placa gráfica:
 * `MeshManager.cleanAttributeMemory` (fragments
 * `FragmentsModels/src/model/mesh-manager.mjs`, `cleanAttributeMemory`
 * e `deleteAttribute`) regista um `attr.onUpload(() => delete this.array)`
 * para `position`, `normal`, `color` e `id`. Sem uma cópia, o
 * deslizador do limiar das arestas e as arestas dos tiles que chegam
 * tarde não têm de onde gerar o `EdgesGeometry`.
 *
 * Porque é que a cópia se faz no `onUpload` e não no `setAttribute`.
 * Fazia-se no `setAttribute` do protótipo, e isso apanhava TODAS as
 * geometrias da aplicação: cada nuvem de pontos ficava com as posições
 * a dobrar (27 bytes por ponto em vez de 15, medido), e as malhas do
 * IFC5 também. Só os atributos a que alguém pede que o array seja
 * apagado depois do envio precisam de cópia, e esses são exactamente
 * os do Fragments. As nuvens, o IFC5 e as próprias arestas nunca
 * chamam `onUpload`, e ficam sem cópia nenhuma.
 *
 * O `setIndex` continua a guardar o índice, mas só de geometrias que já
 * têm cópia da posição (o Fragments guarda o índice depois das posições).
 */
export interface EdgesSourceCache {
  position?: Float32Array
  index?: Uint32Array
  /** Per-vertex localId, used by addEdges to skip meshes that contain
   *  only spatial container items (IfcSpace, IfcBuildingStorey, etc.)
   *  whose bbox geometry would otherwise show as floating wireframes. */
  id?: Float32Array
}

export type GeometryWithEdgesCache = THREE.BufferGeometry & {
  __vn2rEdgesSrc?: EdgesSourceCache
}

type AtributoMarcado = THREE.BufferAttribute & {
  __vn2rGeometria?: GeometryWithEdgesCache
  __vn2rNome?: 'position' | 'id'
}

function copiar(g: GeometryWithEdgesCache, nome: 'position' | 'id', arr: ArrayLike<number>): void {
  if (!g.__vn2rEdgesSrc) g.__vn2rEdgesSrc = {}
  g.__vn2rEdgesSrc[nome] = new Float32Array(arr)
  if (nome === 'position' && !g.__vn2rEdgesSrc.index) {
    const idx = g.index?.array as ArrayLike<number> | undefined
    if (idx && idx.length > 0) g.__vn2rEdgesSrc.index = new Uint32Array(idx)
  }
}

let instalado = false

export function installEdgesSnapshotPatch(): void {
  if (instalado) return
  instalado = true
  const origSetAttribute = THREE.BufferGeometry.prototype.setAttribute
  const origSetIndex = THREE.BufferGeometry.prototype.setIndex
  const origOnUpload = THREE.BufferAttribute.prototype.onUpload

  // Só marca quem é dono do atributo. Não copia nada.
  THREE.BufferGeometry.prototype.setAttribute = function (name, attribute) {
    const result = origSetAttribute.call(this, name, attribute)
    if ((name === 'position' || name === 'id') && attribute instanceof THREE.BufferAttribute) {
      const a = attribute as AtributoMarcado
      a.__vn2rGeometria = this as GeometryWithEdgesCache
      a.__vn2rNome = name
    }
    return result
  }

  // Quem pede para apagar o array depois do envio é o Fragments: é aqui
  // que se copia, antes de o array desaparecer.
  THREE.BufferAttribute.prototype.onUpload = function (callback) {
    const a = this as AtributoMarcado
    const arr = a.array as ArrayLike<number> | undefined
    if (a.__vn2rGeometria && a.__vn2rNome && arr && arr.length > 0) {
      copiar(a.__vn2rGeometria, a.__vn2rNome, arr)
    }
    return origOnUpload.call(this, callback)
  }

  THREE.BufferGeometry.prototype.setIndex = function (index) {
    const result = origSetIndex.call(this, index)
    const g = this as GeometryWithEdgesCache
    if (!g.__vn2rEdgesSrc) return result
    const attr = index instanceof THREE.BufferAttribute ? index : null
    const arr = attr?.array as ArrayLike<number> | undefined
    if (arr && arr.length > 0) g.__vn2rEdgesSrc.index = new Uint32Array(arr)
    return result
  }
}
